/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useState, useContext, useEffect, useMemo} from 'react';
import {Catalog} from 'models/catalog';
import {Box, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Button from 'components/button/Button';
import BlankCard from 'components/BlankCard/BlankCard';
import GqlFormController from 'controllers/GqlFormController/GqlFormController';
import {FormControllerControlProps} from 'controllers/FormController/FormController';
import {AssignationType} from 'models/assignationType';
import withCreateFormViewModel from './createFormViewModel';
import {CREATE_LEAVE_REQUEST} from 'graphql/absences/Mutations';
import LeaveRequestEvidenceController from 'controllers/LeaveRequestEvidenceController';
import {EvidenceType, getEvidenceDescription} from 'models/evidence';
import {
  DEFAULT_MAX_FILE_SIZE_BYTES,
  Mime,
} from 'components/UploadPicker/UploadPicker';
import {
  HOLIDAY,
  ADDITIONAL_LEAVE,
  INABILITY,
  MONETIZATION,
  LeaveRequestType,
} from 'models/leaveRequestType';
import withLeavesViewModel, {
  LeavesModel,
} from 'viewComponents/LeavesBalance/leavesBalanceViewModel';
import {useLazyQuery} from '@apollo/client';
import {
  differenceInBusinessDays,
  differenceInCalendarDays,
  isSameDay,
} from 'date-fns';
import {
  GET_INABILITY_CONFIGURATIONS,
  GET_MONETIZATION_BALANCE,
} from 'graphql/absences/Querys';
import {authContext} from 'contextApi/context/auth';
import {alertContext} from 'contextApi/context/error';
import {useTranslateCatalog} from 'commons/hooks/useTranslateCatalog';

export interface CreateLeaveRequestFormProps {
  leaveTypes: Catalog[];
  variablesMapper: (values: any) => any;
  onSubmit: (mutation: any, data: any) => Promise<any>;
  balance: LeavesModel;
}

interface MonetizationBalanceModel {
  availableDays: number;
  usedDays: number;
  expirationDate?: string | null;
}

type LeaveRangeDateModel = {
  startDate?: Date | null;
  endDate?: Date | null;
  startCheck?: boolean;
  endCheck?: boolean;
};

/** Allowed MIME types for image uploads in evidence files */
const ALLOWED_IMAGE_MIME_TYPES: Mime[] = ['image/jpeg', 'image/png'];

const CreateLeaveRequestForm: React.FC<CreateLeaveRequestFormProps> = ({
  leaveTypes,
  onSubmit: formViewModelOnSubmitHandler,
  variablesMapper,
  balance,
}) => {
  const {t} = useTranslation();
  const {translateCatalogs} = useTranslateCatalog();
  const {userDetails} = useContext(authContext);
  const {dispatch} = useContext(alertContext);
  const [leaveType, setLeaveType] = useState<LeaveRequestType | undefined>();
  const [subLeaveTypes, setSubLeaveTypes] = useState<Catalog[]>();
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState<string | null>(
    null
  );
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [monetizationBalance, setMonetizationBalance] =
    useState<MonetizationBalanceModel>({
      availableDays: 0,
      usedDays: 0,
    });

  // 👇 Query para obtener configuraciones de incapacidades
  const [fetchInabilityConfigurations] = useLazyQuery(
    GET_INABILITY_CONFIGURATIONS,
    {
      variables: {
        country: userDetails?.data?.country,
      },
    }
  );

  const [fetchMonetizationBalance] = useLazyQuery(GET_MONETIZATION_BALANCE);

  // Derive translated catalogs from current input instead of syncing via state.
  // This avoids update loops when translation helpers get a new function reference.
  const translatedLeaveTypes = useMemo(
    () => translateCatalogs(leaveTypes),
    [leaveTypes, translateCatalogs]
  );

  const userId = userDetails?.data?.id;
  const country = userDetails?.data?.country;

  useEffect(() => {
    if (!userId || !country) return;

    fetchMonetizationBalance({variables: {userId, country}})
      .then(({data}) => {
        setMonetizationBalance({
          availableDays: data?.getMonetizationBalance?.availableDays ?? 0,
          usedDays: data?.getMonetizationBalance?.usedDays ?? 0,
          expirationDate: data?.getMonetizationBalance?.expirationDate ?? null,
        });
      })
      .catch(() => {
        setMonetizationBalance({
          availableDays: 0,
          usedDays: 0,
          expirationDate: null,
        });
      });
    // fetchMonetizationBalance is intentionally excluded: useLazyQuery returns a
    // new function reference on every render, which would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, country]);

  // 👇 Handle leave type selection and load sub-types
  useEffect(() => {
    if (!selectedLeaveTypeId) return;

    const selectedLeaveType = translatedLeaveTypes.find(
      (type: any) => Number(type.id) === Number(selectedLeaveTypeId)
    );

    if (!selectedLeaveType) return;

    const leaveTypeName = selectedLeaveType?.name as LeaveRequestType;
    setLeaveType(leaveTypeName);

    // Si es INABILITY, cargar las incapacidades desde el backend
    if (leaveTypeName === INABILITY) {
      fetchInabilityConfigurations()
        .then(({data}) => {
          const inabilityConfigs = data?.getInabilityConfigurations?.data || [];

          // Filtrar solo las activas y mapear al formato Catalog
          const activeInabilities: Catalog[] = inabilityConfigs
            .filter((config: any) => config.status === 'ACTIVE')
            .map((config: any) => ({
              id: config.inabilityType.id,
              name: config.inabilityType.name,
              description: config.inabilityType.description,
            }));

          setSubLeaveTypes(activeInabilities);
        })
        .catch(error => {
          console.error('Error fetching inability configurations:', error);
          setSubLeaveTypes([]);
        });
    } else {
      // Para otros tipos, usar los hijos del catálogo traducido
      const children = selectedLeaveType?.children;
      setSubLeaveTypes(children);
    }
    // fetchInabilityConfigurations is intentionally excluded: useLazyQuery returns a
    // new function reference on every render, which would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeaveTypeId, translatedLeaveTypes]);

  const getLeaveTypeFromValues = (
    values: any
  ): LeaveRequestType | undefined => {
    const selectedId = values?.leaveTypeId;
    if (!selectedId) {
      return leaveType;
    }

    const selectedLeaveType = leaveTypes.find(
      (type: any) => Number(type.id) === Number(selectedId)
    );

    return selectedLeaveType?.name as LeaveRequestType | undefined;
  };

  const validateMonetizationRequest = (values: any): boolean => {
    const currentLeaveType = getLeaveTypeFromValues(values);
    if (currentLeaveType !== MONETIZATION) {
      return true;
    }

    const requestedDays = getRequestedDays(
      values?.leaveRangeDate || {},
      currentLeaveType,
      userDetails?.data?.country
    );
    const vacationAvailable = balance?.availableDays || 0;
    const monetizationAvailable = monetizationBalance.availableDays || 0;

    if (vacationAvailable <= 0 || monetizationAvailable <= 0) {
      dispatch({
        type: 'show',
        payload: {
          level: 'error',
          message: t(
            'leaves.validation.monetization_balance_required',
            'Para monetizar debes contar con días de vacaciones y días monetizables disponibles.'
          ),
        },
      });
      return false;
    }

    if (
      requestedDays > monetizationAvailable ||
      requestedDays > vacationAvailable
    ) {
      dispatch({
        type: 'show',
        payload: {
          level: 'error',
          message: t(
            'leaves.validation.monetization_requested_days_exceeded',
            'No puedes solicitar {{requested}} dias. Disponibles para monetizar: {{monetizationAvailable}}. Vacaciones disponibles: {{vacationAvailable}}.',
            {
              requested: requestedDays,
              monetizationAvailable,
              vacationAvailable,
            }
          ),
        },
      });
      return false;
    }

    return true;
  };

  const watchForLeaveRequestType = (leaveRequest: any) => {
    const nextLeaveTypeId = leaveRequest.leaveTypeId ?? null;
    const nextStartDate = leaveRequest.leaveRangeDate?.startDate || null;

    // Avoid redundant state updates from repeated watch callbacks.
    setSelectedLeaveTypeId(prev =>
      prev === nextLeaveTypeId ? prev : nextLeaveTypeId
    );
    setSelectedStartDate(prev => {
      if (prev === nextStartDate) return prev;
      if (prev && nextStartDate && prev.getTime() === nextStartDate.getTime()) {
        return prev;
      }
      return nextStartDate;
    });
  };

  const getRequestedDays = (
    leaveRangeDate: LeaveRangeDateModel,
    leaveTypeParam?: LeaveRequestType,
    countryCode?: string
  ): number => {
    const startDate = leaveRangeDate?.startDate;
    const endDate = leaveRangeDate?.endDate;

    if (!startDate || !endDate) return 0;

    // For Brazil with specific leave types, count natural days (including weekends)
    // Otherwise, count only business days
    const useNaturalDays =
      countryCode === 'BRA' &&
      leaveTypeParam &&
      [HOLIDAY, MONETIZATION, ADDITIONAL_LEAVE].includes(leaveTypeParam);

    let requestedDays = useNaturalDays
      ? differenceInCalendarDays(endDate, startDate) + 1
      : differenceInBusinessDays(endDate, startDate) + 1;

    if (requestedDays < 0) {
      requestedDays = 0;
    }

    if (isSameDay(startDate, endDate)) {
      if (leaveRangeDate.startCheck || leaveRangeDate.endCheck) {
        requestedDays = 0.5;
      }
      return requestedDays;
    }

    if (leaveRangeDate.startCheck) {
      requestedDays -= 0.5;
    }
    if (leaveRangeDate.endCheck) {
      requestedDays -= 0.5;
    }

    return Math.max(requestedDays, 0);
  };

  const handleOnSubmit = (mutationPromise: any, data: any) => {
    if (!validateMonetizationRequest(data?.formData || {})) {
      return Promise.reject(new Error('Invalid monetization request'));
    }

    return formViewModelOnSubmitHandler(mutationPromise, data);
  };

  const controls: Array<
    FormControllerControlProps | FormControllerControlProps[]
  > = [
    {
      name: 'leaveTypeId',
      type: 'select',
      options: {
        required: true,
      },
      props: {
        label: t('leaves.type'),
        placeholder: t('common.choose_an_option'),
        className: 'mt-2',
        noSelection: t('common.choose_an_option'),
        children: translatedLeaveTypes
          .filter(
            (leaveType: any) =>
              leaveType.name !== ADDITIONAL_LEAVE ||
              (balance?.additionalLeaveDays || 0) > 0
          )
          .map((leaveType: any) => ({
            key: leaveType.id,
            description: leaveType.description,
          })),
      },
    },
    {
      name: 'subLeaveTypeId',
      type: 'select',
      options: {
        required: !!subLeaveTypes?.length,
      },
      props: {
        key: leaveType || 'default',
        label: t('leaves.subType'),
        placeholder: t('common.choose_an_option'),
        className: 'mt-2',
        noSelection: t('common.choose_an_option'),
        disabled: !subLeaveTypes?.length,
        defaultValue: '',
        children: (subLeaveTypes || []).map((subLeaveType: any) => ({
          key: subLeaveType.id,
          description: subLeaveType.description,
        })),
      },
    },
    {
      name: 'leaveRangeDate',
      type: 'leaveDateRangePicker',
      options: {
        required: true,
      },
      props: {
        labels: {
          startDateLabel: t('leaves.MyLeaves.inputLabels.startDateLabel'),
          endDateLabel: t('leaves.MyLeaves.inputLabels.endDateLabel'),
          startCheckLabel: t('leaves.MyLeaves.inputLabels.startCheckLabel'),
          endCheckLabel: t('leaves.MyLeaves.inputLabels.endCheckLabel'),
          optionalStartCheckLabel: t(
            'leaves.MyLeaves.inputLabels.optionalStartCheckLabel'
          ),
          optionalEndCheckLabel: t(
            'leaves.MyLeaves.inputLabels.optionalEndCheckLabel'
          ),
        },
        withChecks: true,
        leaveType: leaveType,
        // Show past-date advisory when the selected start date is before today,
        // otherwise show the early-date advisory when it is within 30 days
        earlyDateWarning: (() => {
          if (selectedStartDate === null) return undefined;
          const daysDiff = differenceInCalendarDays(
            selectedStartDate,
            new Date()
          );
          if (daysDiff < 0) return t('leaves.MyLeaves.pastStartDateWarning');
          if (daysDiff < 30) return t('leaves.MyLeaves.earlyStartDateWarning');
          return undefined;
        })(),
      },
    },
  ];

  // Two separate evidence controls shown only after a leave type is selected:
  // - First slot: customer evidence (optional)
  // - Second slot: manager evidence (optional only for monetization)
  const managerEvidenceControls: Array<
    FormControllerControlProps | FormControllerControlProps[]
  > = leaveType
    ? [
        {
          name: EvidenceType.CUSTOMER_EVIDENCE,
          type: 'file',
          options: {required: false},
          props: {
            label: t(getEvidenceDescription(EvidenceType.CUSTOMER_EVIDENCE)),
            className: 'mt-2',
            // Restrict to images and cap at 5 MB for evidence uploads
            allowedMimeTypes: ALLOWED_IMAGE_MIME_TYPES,
            maxFileSizeBytes: DEFAULT_MAX_FILE_SIZE_BYTES,
          },
        },
        {
          name: EvidenceType.MANAGER_EVIDENCE,
          type: 'file',
          options: {required: leaveType !== MONETIZATION},
          props: {
            label: t(getEvidenceDescription(EvidenceType.MANAGER_EVIDENCE)),
            className: 'mt-2',
            // Restrict to images and cap at 5 MB for evidence uploads
            allowedMimeTypes: ALLOWED_IMAGE_MIME_TYPES,
            maxFileSizeBytes: DEFAULT_MAX_FILE_SIZE_BYTES,
          },
        },
      ]
    : [];

  return (
    <BlankCard>
      <Stack spacing={2}>
        <Typography variant="h5">{t('leaves.new_request')}</Typography>
        <LeaveRequestEvidenceController
          assignationType={AssignationType.PROJECT}
          leaveType={leaveType}
          optionalEvidenceTypes={[EvidenceType.MEDICAL_PRESCRIPTION]}
        >
          {({evidenceFormControls}) => {
            // Remove static evidence types from dynamic controls to avoid duplication
            const additionalControls = evidenceFormControls.filter(
              c =>
                c.name !== EvidenceType.MANAGER_EVIDENCE &&
                c.name !== EvidenceType.CUSTOMER_EVIDENCE
            );
            // Include the static manager evidence fields only when a leave type is selected
            const staticEvidenceControls: Array<
              FormControllerControlProps | FormControllerControlProps[]
            > = managerEvidenceControls;
            return (
              <GqlFormController
                mutation={CREATE_LEAVE_REQUEST}
                beforeSubmit={validateMonetizationRequest}
                variablesMapper={variablesMapper}
                className="flex flex-col"
                onSubmit={handleOnSubmit}
                watch={watchForLeaveRequestType}
                itemProps={{xs: 12}}
                controls={controls.concat(
                  staticEvidenceControls,
                  additionalControls
                )}
              >
                {({onSubmit}: any) => (
                  <Box
                    className="mt-4 w-full"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    {/* Legend left-aligned, buttons pushed to the right */}
                    <Typography variant="caption" color="text.secondary">
                      {t('leaves.required_fields_legend')}
                    </Typography>
                    <Box sx={{display: 'flex', flexDirection: 'row'}}>
                      <Button
                        name="save_draft"
                        onClick={() => {
                          onSubmit({
                            isDraft: true,
                            useSubLeaveType: leaveType === INABILITY,
                          })();
                        }}
                        buttonStyle="primaryOutline"
                        text={t('leaves.save_draft')}
                        sx={{marginRight: '8px !important'}}
                      />
                      <Button
                        onClick={() => {
                          onSubmit({
                            isDraft: false,
                            useSubLeaveType: leaveType === INABILITY,
                          })();
                        }}
                        text={t('leaves.send_request')}
                      />
                    </Box>
                  </Box>
                )}
              </GqlFormController>
            );
          }}
        </LeaveRequestEvidenceController>
      </Stack>
    </BlankCard>
  );
};

export default withLeavesViewModel()(
  withCreateFormViewModel()(CreateLeaveRequestForm)
);
